// imports
import { FC, memo, useCallback, useMemo, useState } from 'react';
// components
import InfiniteSelect from './InfiniteSelect';
// interfaces, constants, graphql
import { HTTP_STATUS } from 'constants/index';
import { DensitySelectProps, SelectType } from 'interfaces';
import { DensitiesPayload, useFindAllDensitySelectorLazyQuery } from 'generated/graphql';

const DensitySelect: FC<DensitySelectProps> = ({
  name,
  title,
  value,
  handleChange,
  margin = 'dense',
  isClearable = false,
  ...rest
}) => {
  const [totalPages, setTotalPages] = useState(0);
  const [getLoading, setGetLoading] = useState<boolean>(false);
  const [options, setOptions] = useState<DensitiesPayload['data']>([]);

  const updatedOptions = useMemo(() => {
    if (options?.length) {
      return options?.map<SelectType>((item) => {
        const { id, name, description } = item || {};

        return {
          value: id,
          name: name || '',
          description: description || '',
        };
      });
    }
    return [];
  }, [options]);

  const [fetchSelectorData, { loading }] = useFindAllDensitySelectorLazyQuery({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,

    onCompleted: (data) => {
      const { findAllDensities } = data;
      const { data: densities, response, pagination } = findAllDensities || {};
      const { status } = response || {};
      if (status === HTTP_STATUS.SUCCESS) {
        const { totalPages: pages } = pagination || {};
        setTotalPages(pages);
        setOptions([
          ...(getLoading ? [] : options || []),
          ...((densities as DensitiesPayload['data']) ?? []),
        ]);
        setGetLoading(false);
      } else {
        setGetLoading(false);
      }
    },
    onError: () => {
      setOptions([]);
      setGetLoading(false);
    },
  });

  const fetchData = useCallback(
    async (page?: number, query?: string) => {
      if (!page) setGetLoading(true);
      await fetchSelectorData({
        variables: {
          findAllDensitiesInput: {
            ...(query && { search: query }),
            paginationOptions: { page: page || 1, limit: 10 },
          },
        },
      });
    },
    [fetchSelectorData],
  );

  return (
    <InfiniteSelect
      {...rest}
      name={name}
      title={title}
      value={value}
      margin={margin}
      loading={loading}
      fetchData={fetchData}
      setOptions={setOptions}
      totalPages={totalPages}
      getLoading={getLoading}
      options={updatedOptions}
      isClearable={isClearable}
      handleChange={handleChange}
    />
  );
};

export default memo(DensitySelect);
